import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { NameIdObj, NameIdObjString } from 'common/models/GenericTypes';
import CallOutNotification from 'components/CallOutNotification';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetApplyFlowClassifications,
  GetApplyFlowLocationsLookup,
} from 'services/JobBoardsService';

interface Props {
  jobBoardId: number;
  advertId: number;
  attributes?: Record<string, string>;
  advertRecordAttributes?: Record<string, string>;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  attributeChangeHandler?: (jobBoardId: number, key: string, value: string) => void;
  validationMessageHandler?: (jobBoardId: number, message: string) => void;
}

export default function ApplyFlowEdit({
  jobBoardId,
  advertId,
  attributes,
  advertRecordAttributes,
  loadingHandler,
  errorHandler,
  attributeChangeHandler,
  validationMessageHandler,
}: Props) {
  const [classificationOptions, setClassificationOptions] = useState<NameIdObjString[]>([]);
  const [subClassificationMap, setSubClassificationMap] = useState<
    Record<string, NameIdObjString[]>
  >({});
  const [lookupTerm, setLookupTerm] = useState('');
  const [locationOptions, setLocationOptions] = useState<NameIdObj[]>([]);
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    const getSetupData = async () => {
      loadingHandler?.(true);
      const res = await GetApplyFlowClassifications(jobBoardId, advertId, errorHandler);
      if (res) {
        const keys = Object.keys(res);
        if (keys.length === 0) {
          setInfoMessage('No Categories or Sub Categories available from ApplyFlow');
          loadingHandler?.(false);
          return;
        }

        const c1Opts: NameIdObjString[] = [];
        const c2Map: Record<string, NameIdObjString[]> = {};
        for (let i = 0; i < keys.length; i++) {
          const k = keys[i];
          const c1 = res[k];
          if (c1) {
            c1Opts.push({ id: c1.classification_id, name: c1.classification_label });
            if (c1.subclassifications && c1.subclassifications.length > 0) {
              c2Map[c1.classification_id] = c1.subclassifications.map((c) => ({
                id: c.subclassification_id,
                name: c.subclassification_label,
              }));
            }
          }
        }

        setClassificationOptions(c1Opts);
        setSubClassificationMap(c2Map);
      }
      loadingHandler?.(false);
    };
    getSetupData();
  }, [advertId, errorHandler, jobBoardId, loadingHandler]);

  const atts = useMemo<Record<string, string>>(() => {
    if (attributes) return attributes;
    return {};
  }, [attributes]);

  const recordAtts = useMemo<Record<string, string>>(() => {
    if (advertRecordAttributes) return advertRecordAttributes;
    return {};
  }, [advertRecordAttributes]);

  const workType = useMemo(() => recordAtts.workType ?? '', [recordAtts.workType]);
  const salaryType = useMemo(() => recordAtts.salaryType ?? '', [recordAtts.salaryType]);
  const salaryFrom = useMemo(() => recordAtts.salaryFrom ?? '', [recordAtts.salaryFrom]);
  const salaryTo = useMemo(() => recordAtts.salaryTo ?? '', [recordAtts.salaryTo]);
  const displaySalary = useMemo(() => recordAtts.displaySalary ?? '', [recordAtts.displaySalary]);

  const classificationId = useMemo(() => atts.ClassificationId ?? '', [atts.ClassificationId]);

  const subclassificationId = useMemo(
    () => atts.SubclassificationId ?? '',
    [atts.SubclassificationId]
  );

  const locationId = useMemo(() => atts.LocationId ?? '', [atts.LocationId]);
  const locationName = useMemo(() => atts.LocationName ?? '', [atts.LocationName]);

  useEffect(
    () => attributeChangeHandler && attributeChangeHandler(jobBoardId, 'workType', workType),
    [attributeChangeHandler, workType, jobBoardId]
  );
  useEffect(
    () => attributeChangeHandler && attributeChangeHandler(jobBoardId, 'salaryType', salaryType),
    [attributeChangeHandler, salaryType, jobBoardId]
  );
  useEffect(
    () => attributeChangeHandler && attributeChangeHandler(jobBoardId, 'salaryFrom', salaryFrom),
    [attributeChangeHandler, salaryFrom, jobBoardId]
  );
  useEffect(
    () => attributeChangeHandler && attributeChangeHandler(jobBoardId, 'salaryTo', salaryTo),
    [attributeChangeHandler, salaryTo, jobBoardId]
  );
  useEffect(
    () =>
      attributeChangeHandler && attributeChangeHandler(jobBoardId, 'displaySalary', displaySalary),
    [attributeChangeHandler, displaySalary, jobBoardId]
  );

  useEffect(() => {
    if (validationMessageHandler) {
      if (!locationId && !locationName)
        validationMessageHandler(jobBoardId, 'A Location MUST be selected for Apply Flow');
      else validationMessageHandler(jobBoardId, '');
    }
  }, [jobBoardId, locationId, locationName, validationMessageHandler]);

  const children = useMemo(() => {
    return subClassificationMap[classificationId] ?? [];
  }, [classificationId, subClassificationMap]);

  const selectedParent = useMemo(() => {
    const item = classificationOptions.find((c) => c.id === classificationId);
    if (item) return item;
    return null;
  }, [classificationOptions, classificationId]);

  const selectedChild = useMemo(() => {
    const item = children.find((c) => c.id === subclassificationId);
    if (item) return item;
    return null;
  }, [subclassificationId, children]);

  const classificationChangeHandler = useCallback(
    (e: any, v: NameIdObjString | null) => {
      attributeChangeHandler &&
        attributeChangeHandler(jobBoardId, 'ClassificationId', v ? v.id : '');
      attributeChangeHandler &&
        attributeChangeHandler(jobBoardId, 'ClassificationName', v ? v.name : '');
    },
    [jobBoardId, attributeChangeHandler]
  );

  const subClassificationChangeHandler = useCallback(
    (e: any, v: NameIdObjString | null) => {
      attributeChangeHandler &&
        attributeChangeHandler(jobBoardId, 'SubclassificationId', v ? v.id : '');
      attributeChangeHandler &&
        attributeChangeHandler(jobBoardId, 'SubclassificationName', v ? v.name : '');
    },
    [jobBoardId, attributeChangeHandler]
  );

  useEffect(() => {
    if (lookupTerm.length >= 3) {
      const delaySearch = setTimeout(async () => {
        loadingHandler && loadingHandler(true);
        const res = await GetApplyFlowLocationsLookup(
          jobBoardId,
          advertId,
          lookupTerm,
          errorHandler
        );
        if (res && res.data && res.data.length > 0) {
          setLocationOptions(res.data.map((l) => ({ id: l.id, name: l.label })));
        }
        loadingHandler && loadingHandler(false);
      }, 300);
      return () => clearTimeout(delaySearch);
    }
  }, [lookupTerm, jobBoardId, advertId, errorHandler, loadingHandler]);

  const handleInputChange = useCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      reason !== 'reset' && setLookupTerm(value);
    },
    []
  );

  const selectedLocation = useMemo<NameIdObj | null>(() => {
    if (locationId && locationName) return { id: +locationId, name: locationName };
    return null;
  }, [locationId, locationName]);

  const locationChangeHandler = useCallback(
    (e: any, v: NameIdObj | null) => {
      attributeChangeHandler &&
        attributeChangeHandler(jobBoardId, 'LocationId', v ? v.id.toString() : '');
      attributeChangeHandler && attributeChangeHandler(jobBoardId, 'LocationName', v ? v.name : '');
    },
    [jobBoardId, attributeChangeHandler]
  );

  return (
    <>
      {Boolean(infoMessage) && <CallOutNotification type="info" title={infoMessage} />}
      <Box display="flex" gap={2} pt={2}>
        <Box display="flex" flexDirection="column" gap={2} width="50%">
          <Autocomplete
            size="small"
            value={selectedParent}
            options={classificationOptions}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={classificationChangeHandler}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Classification"
                slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
              />
            )}
          />
          <Autocomplete
            size="small"
            value={selectedChild}
            options={children}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={subClassificationChangeHandler}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Classification"
                slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
              />
            )}
          />
          <Autocomplete
            size="small"
            fullWidth
            options={locationOptions}
            value={selectedLocation}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            onChange={locationChangeHandler}
            noOptionsText="Type to search"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
              />
            )}
            onInputChange={handleInputChange}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap={2} width="50%">
          <TextField disabled label="Work Type" value={workType} />
          <TextField disabled label="Salary Type" value={salaryType} />
          <Box display="flex" gap={2}>
            <TextField disabled label="Salary From" sx={{ flex: '1 1 0' }} value={salaryFrom} />
            <TextField disabled label="Salary To" sx={{ flex: '1 1 0' }} value={salaryTo} />
          </Box>
          <TextField disabled label="Display Salary" value={displaySalary} />
        </Box>
      </Box>
    </>
  );
}
